import { DebtCollectionTaskEventStatusEnum } from '@zaoblako/shared/modules/debts/enums/debt-collection-task-event-status.enum';
import { PlanningUnitFindDto, type PlanningUnitFindInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-find.dto';
import {
  DebtCollectionTaskEventTypeFindDto,
  type DebtCollectionTaskEventTypeFindInterface,
} from '@zaoblako/shared/modules/debts/dtos/debt-collection-task-event-type/debt-collection-task-event-type-find.dto';
import { MasterMilestoneFindDto, type MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ReminderFindDto, type ReminderFindInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { DebtsDtoKeysEnum } from '@zaoblako/shared/modules/debts/enums/debts-dto-keys.enum';
import type { DebtCollectionTaskFindInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-find.dto';

import * as yup from 'yup';

/** Debt collection task event response body - DTO signature */
export interface DebtCollectionTaskEventFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;

  // Tech columns
  /** Task event creation date and time */
  created: Date;
  /** Task event last update date and time */
  updated: Date;
  /** Task event soft deletion date and time */
  deleted?: Date;

  // Base data column
  /** Task event display name */
  name: string;
  /** Task event additional description */
  description?: string;

  // Period columns
  /** Task event start date and time (current by default) */
  startAt?: Date;
  /** Task event start date and time (current plus one hour by default) */
  endAt?: Date;
  /** Expired event mark. Calculated automatically. */
  expired?: boolean;

  // Status columns
  /** Task event status */
  status: DebtCollectionTaskEventStatusEnum;
  /** Finished task event mark. Calculated automatically. */
  checked?: boolean;
  /** Task event finish timestamp */
  finishedAt?: Date;
  /** Task event finish comment */
  finishComment?: string;
  /** Task event repeat schedule */
  planning?: PlanningUnitFindInterface;
  /** Related task */
  task: DebtCollectionTaskFindInterface;
  /** Task event type */
  type: DebtCollectionTaskEventTypeFindInterface;
  /** Related master milestone */
  masterMilestone?: MasterMilestoneFindInterface;
  /** Task event creator */
  createdBy?: UserFindInterface;
  /** Task event responsible users */
  responsible?: UserFindInterface[];
  /** Reminder for event responsible users */
  reminder?: ReminderFindInterface;
}

/** Debt collection task event response body - DTO schema */
export const DebtCollectionTaskEventFindDto = SerdeDtoClass.create<DebtCollectionTaskEventFindInterface>(DebtsDtoKeysEnum.DEBT_COLLECTION_TASK_EVENT_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Дата создания',
    serializer: yup.date(),
  },
  updated: {
    label: 'Дата последнего изменения',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Дата удаления',
    serializer: yup.date(),
  },
  name: {
    label: 'Название',
    serializer: yup.string(),
  },
  description: {
    optional: true,
    label: 'Описание',
    serializer: yup.string(),
  },
  startAt: {
    optional: true,
    label: 'Дата начала',
    serializer: yup.date(),
  },
  endAt: {
    optional: true,
    label: 'Планируемая дата завершения',
    serializer: yup.date(),
  },
  expired: {
    optional: true,
    label: 'Просрочено',
    serializer: yup.boolean(),
  },
  status: {
    label: 'Статус',
    serializer: yup
      .string()
      .oneOf(Object.values(DebtCollectionTaskEventStatusEnum)),
  },
  checked: {
    optional: true,
    label: 'Завершено',
    serializer: yup.boolean(),
  },
  finishedAt: {
    optional: true,
    label: 'Дата фактического завершения',
    serializer: yup.date(),
  },
  finishComment: {
    optional: true,
    label: 'Дополнительный комментарий к завершению события',
    serializer: yup.string(),
  },
  planning: {
    optional: true,
    nested: PlanningUnitFindDto,
    label: 'Настройки повтора события',
    serializer: (_, nested) => nested,
  },
  task: {
    label: 'Задача, к которой относится событие',
    nested: DebtsDtoKeysEnum.DEBT_COLLECTION_TASK_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  type: {
    label: 'Тип события',
    nested: DebtCollectionTaskEventTypeFindDto,
    serializer: (_, nested) => nested,
  },
  masterMilestone: {
    optional: true,
    label: 'Эпик',
    nested: MasterMilestoneFindDto,
    serializer: (_, nested) => nested,
  },
  createdBy: {
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  responsible: {
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  reminder: {
    optional: true,
    label: 'Напоминание',
    nested: ReminderFindDto,
    serializer: (_, nested) => nested,
  },
});
